import { useEffect, useRef, useState } from 'react';
import { chatService, type AuthAccount } from '@services/chatService';

export interface UserInfo extends AuthAccount {
  permissions?: Record<string, boolean>;
  isOwner?: boolean;
}

interface UseGetUserInfoReturn {
  user?: UserInfo | null;
}

export const useGetUserInfo = (token: string | null): UseGetUserInfoReturn => {
  const [user, setUser] = useState<UserInfo | null>(null);
  const fetchIdRef = useRef(0);

  useEffect(() => {
    let cancelled = false;

    const fetchUserInfo = async () => {
      const currentFetchId = ++fetchIdRef.current;

      if (!token) {
        if (!cancelled && currentFetchId === fetchIdRef.current) {
          setUser(null);
        }
        return;
      }

      try {
        const userInfoRes = await chatService.getUserInfo(token);
        if (cancelled || currentFetchId !== fetchIdRef.current || !userInfoRes) {
          return;
        }

        const basicUserInfo: UserInfo = userInfoRes.account;

        try {
          const permissionRes = await chatService.getUserPermission(token);
          if (cancelled || currentFetchId !== fetchIdRef.current) {
            return;
          }

          setUser({
            ...basicUserInfo,
            permissions: permissionRes?.permissions,
            isOwner: permissionRes?.isOwner,
          });
        } catch (permError) {
          console.warn('Failed to fetch user permissions:', permError);
          if (!cancelled && currentFetchId === fetchIdRef.current) {
            setUser(basicUserInfo);
          }
        }
      } catch (error) {
        console.error('Failed to fetch user info:', error);
        if (!cancelled && currentFetchId === fetchIdRef.current) {
          setUser(null);
        }
      }
    };

    fetchUserInfo();

    return () => {
      cancelled = true;
    };
  }, [token]);

  return { user };
};
